// src/components/mfa/MfaModalShell.jsx
//
// The frame every two-step verification dialog uses. Same overlay, panel and
// header as BICredentialsModal so the security dialogs look like the rest of
// Settings. No click-outside-to-close: a half-finished setup or an unread set
// of recovery codes should not vanish on a stray click.
import React from 'react';
import { AlertCircle } from 'lucide-react';

const TONES = {
  blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
  green: 'bg-green-500/10 border-green-500/30 text-green-400',
  red: 'bg-red-500/10 border-red-500/30 text-red-400',
  amber: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
};

export default function MfaModalShell({ icon: Icon, tone = 'blue', title, subtitle, step, children, footer }) {
  const titleId = `mfa-dialog-${String(title).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-lg bg-[#0f0f0f] border border-[#2a2a2a] rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-[#2a2a2a]">
          <div className="flex items-start gap-3">
            <div className={`h-10 w-10 rounded-lg border flex items-center justify-center flex-shrink-0 ${TONES[tone] || TONES.blue}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="flex-1">
              {step && (
                <div className="text-xs text-[#a0a0a0] uppercase tracking-wide mb-1">{step}</div>
              )}
              <h3 id={titleId} className="text-lg font-semibold text-white">{title}</h3>
              {subtitle && <p className="text-sm text-[#a0a0a0] mt-0.5">{subtitle}</p>}
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">{children}</div>

        {footer && (
          <div className="px-6 pb-6 border-t border-[#2a2a2a] pt-4">{footer}</div>
        )}
      </div>
    </div>
  );
}

export function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg" role="alert">
      <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
      <p className="text-sm text-red-400">{message}</p>
    </div>
  );
}
